import type Phaser from 'phaser';
import { Dir, dx, dy, type Direction } from '../../sim/direction';
import type { SteerIntent } from '../../sim/mower';
import { label, UI } from '../ui/text';
import type { TouchTrace } from './InputController';

/** How long a finger path and its swipe markers stay on screen. */
const FADE_MS = 1500;
const DIR_LETTER: Record<Direction, string> = {
  [Dir.N]: 'N',
  [Dir.E]: 'E',
  [Dir.S]: 'S',
  [Dir.W]: 'W',
};

interface Mark {
  x: number;
  y: number;
  at: number;
}

/**
 * Steering diagnostics, on with `?touches` in any build (so it works on a phone against the
 * hosted game): draws each finger path, an arrow where a swipe fired, and the sim's pending and
 * queued turns. For tuning swipe feel on real devices (lm-d4m.2).
 */
export class TouchDebug {
  private readonly gfx: Phaser.GameObjects.Graphics;
  private readonly status: Phaser.GameObjects.BitmapText;
  private readonly paths = new Map<number, Mark[]>();
  private readonly swipes: (Mark & { dir: Direction })[] = [];
  private readonly taps: Mark[] = [];

  static enabled(): boolean {
    return new URLSearchParams(window.location.search).has('touches');
  }

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly intents: () => { pending: SteerIntent | null; queued: SteerIntent | null },
  ) {
    this.gfx = scene.add.graphics().setScrollFactor(0).setDepth(1000);
    this.status = label(scene, 2, 2, '', 1, UI.accent).setScrollFactor(0).setDepth(1000);
  }

  readonly trace = (t: TouchTrace): void => {
    const at = this.scene.time.now;
    const { x, y } = this.toCamera(t.x, t.y);
    if (t.kind === 'gesture') {
      if (t.gesture.kind === 'swipe') this.swipes.push({ x, y, at, dir: t.gesture.dir });
      else this.taps.push({ x, y, at });
    } else if (t.kind === 'down') this.paths.set(t.id, [{ x, y, at }]);
    else this.paths.get(t.id)?.push({ x, y, at });
  };

  /** Call every frame. */
  render(): void {
    const now = this.scene.time.now;
    const live = (m: Mark) => now - m.at < FADE_MS;
    const alpha = (m: Mark) => 1 - (now - m.at) / FADE_MS;
    const g = this.gfx.clear();

    for (const [id, path] of this.paths) {
      const kept = path.filter(live);
      if (kept.length === 0) {
        this.paths.delete(id);
        continue;
      }
      this.paths.set(id, kept);
      for (let i = 1; i < kept.length; i++) {
        g.lineStyle(1, 0xffffff, alpha(kept[i]!) * 0.8);
        g.lineBetween(kept[i - 1]!.x, kept[i - 1]!.y, kept[i]!.x, kept[i]!.y);
      }
    }
    prune(this.swipes, live);
    for (const s of this.swipes) {
      // Arrow from the fire point in the swipe direction.
      const ex = s.x + dx(s.dir) * 8;
      const ey = s.y + dy(s.dir) * 8;
      g.lineStyle(2, UI.accent, alpha(s));
      g.lineBetween(s.x, s.y, ex, ey);
      g.fillStyle(UI.accent, alpha(s)).fillCircle(ex, ey, 2);
    }
    prune(this.taps, live);
    for (const t of this.taps) g.lineStyle(1, 0x7fd4ff, alpha(t)).strokeCircle(t.x, t.y, 4);

    const { pending, queued } = this.intents();
    this.status.setText(`P ${describe(pending)}  Q ${describe(queued)}`);
  }

  /** Pointer coordinates are screen pixels; draw relative to the (offset) camera viewport. */
  private toCamera(x: number, y: number): { x: number; y: number } {
    const cam = this.scene.cameras.main;
    return { x: x - cam.x, y: y - cam.y };
  }
}

function describe(intent: SteerIntent | null): string {
  if (!intent) return '-';
  return intent.type === 'face' ? DIR_LETTER[intent.dir] : intent.turn.toUpperCase();
}

function prune<T>(list: T[], keep: (item: T) => boolean): void {
  let n = 0;
  for (const item of list) if (keep(item)) list[n++] = item;
  list.length = n;
}
